// Which tempo to suggest: what was heard (listen.ts) or what the names say.
//
// The detector is the room's own (`detectTempo`, web-shared/src/lib/warp.ts):
// onset envelope, autocorrelated over 60–200 BPM, folded into 80–160. That
// fold is right for most music and wrong for half- and double-time genres
// (170 drum & bass reads 85, 70 trap reads 140), so the form offers ½× and 2×
// beside an audio guess — and the guess is always editable.
//
// Length is no help for stems: an export from bar 1 usually carries a tail,
// and a three-minute file lands on a whole bar every few BPM anyway. (Loops
// are different — Phase 2's clip library can read tempo off their length.)

import { CONFIDENT_TEMPO } from "@web-shared/lib/warp";

import type { UploadKind } from "./classify";

/**
 * Where the suggested BPM came from: the file/folder names, the audio, the
 * remixed seed's own tempo, or nowhere (`typed` — the producer filled it in).
 */
export type BpmSource = "names" | "audio" | "parent" | "typed";

export interface BpmSuggestion {
  bpm: number | null;
  source: BpmSource;
}

/** Stems tried, best candidates first. */
const MAX_LISTENED = 2;

export const BEAT_HINT = /(drum|kick|beat|perc|hat|snare|clap|loop|break|groove|top)/i;

/** Which stems to listen to: drum-named ones first, then the rest in order. */
export function listenOrder(names: string[]): number[] {
  const idx = names.map((_, i) => i);
  const beat = idx.filter((i) => BEAT_HINT.test(names[i]!));
  const rest = idx.filter((i) => !BEAT_HINT.test(names[i]!));
  return [...beat, ...rest].slice(0, MAX_LISTENED);
}

/** Producers pick whole tempos; a detector's 127.94 is 128. */
export function roundBpm(bpm: number): number {
  return Math.round(bpm);
}

/** The most confident whole-number tempo among some estimates, or null. */
export function bestEstimate(estimates: Array<{ bpm: number; confidence: number } | null>): number | null {
  let best: { bpm: number; confidence: number } | null = null;
  for (const e of estimates) {
    if (e && e.confidence >= CONFIDENT_TEMPO && (!best || e.confidence > best.confidence)) best = e;
  }
  return best ? roundBpm(best.bpm) : null;
}

/**
 * The tempo to start the form from, given what the names say and what was
 * heard (listen.ts).
 *
 * Stems from one project: the audio, since a track's name carries whatever
 * tempo its sample came in at (an acapella's "90 Bmaj" in a 160 project), so
 * no track name is trusted — only the folder's, which names the project. An
 * unsure hearing still beats nothing; the form offers ½× and 2× beside it.
 * Loose files: the names first, which are the files' own.
 */
export function suggestBpm(
  kind: UploadKind,
  names: { folder: number | null; any: number | null },
  heard: { bpm: number; confidence: number } | null,
): BpmSuggestion {
  const confident = heard !== null && heard.confidence >= CONFIDENT_TEMPO;
  if (kind === "stems") {
    if (confident) return { bpm: roundBpm(heard.bpm), source: "audio" };
    if (names.folder !== null) return { bpm: names.folder, source: "names" };
  } else if (names.any !== null) {
    return { bpm: names.any, source: "names" };
  }
  if (heard !== null) return { bpm: roundBpm(heard.bpm), source: "audio" };
  return { bpm: null, source: "typed" };
}
